import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  Stack,
  useTheme,
} from "@mui/material";
import BackButton from "components/navigation/back-button";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { ShowMore } from "components/shared/show-more";
import { StyledMarkdown } from "components/shared/styled-markdown/styled-markdown";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { DatasetDefinitions } from "data/models";
import {
  fetchCatalogueEntry,
  fetchDefinitions,
  fetchUserLicences,
} from "data/queries";
import { Product } from "pages/geoscape-data/constants";
import { useEffect, useState } from "react";
import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import CustomButtonGroup from "./CustomButtons";
import { CatalogueEntryCaseStudies } from "./catalogue-entry-case-studies";
import { CatalogueEntryDataDictionary } from "./catalogue-entry-data-dictionary";
import { CatalogueEntryMetadata } from "./catalogue-entry-metadata";

// Reverse mapping for NATIONAL X back to backend keys
const REVERSE_PRODUCT_MAPPING: Record<string, string> = {
  "NATIONAL PLANNING": "GEOSCAPE PLANNING",
  "NATIONAL PLANNING INSIGHTS": "GEOSCAPE PLANNING INSIGHTS",
  "NATIONAL BUILDINGS": "GEOSCAPE BUILDINGS",
  "NATIONAL CADASTRE": "GEOSCAPE CADASTRE",
  "NATIONAL PROPERTY": "GEOSCAPE PROPERTY",
  "NATIONAL TREES": "GEOSCAPE TREES",
  "NATIONAL ROADS": "GEOSCAPE ROADS",
  "NATIONAL AIRPORTS": "GEOSCAPE AIRPORTS",
  "NATIONAL RAIL": "GEOSCAPE RAIL",
  "NATIONAL LOCALITIES": "GEOSCAPE LOCALITIES",
  "NATIONAL LOCAL GOVERNMENT AREAS": "GEOSCAPE LOCAL GOVERNMENT AREAS",
  "NATIONAL WARDS": "GEOSCAPE WARDS",
  "NATIONAL SOLAR": "GEOSCAPE SOLAR",
  "NATIONAL SURFACE COVER": "GEOSCAPE SURFACE COVER",
  "NATIONAL GREENSPACE": "GEOSCAPE GREENSPACE",
  "NATIONAL HYDROLOGY": "GEOSCAPE HYDROLOGY",
  "NATIONAL ELECTORAL BOUNDARIES": "ELECTORAL BOUNDARIES",
  "NATIONAL POSTCODE BOUNDARIES": "POSTCODE BOUNDARIES",
};

export const CatalogueEntry = () => {
  let { productName }: any = useParams();
  const [identityState] = useIdentity();

  const [licenced, setLicenced] = useState<boolean>(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const catalogueEntryQuery = useQuery({
      queryKey: [QueryKeys.catalogueEntryKey, productName],
      queryFn: () => fetchCatalogueEntry(productName),
    });

  const userLicences = useQuery({
      queryKey: [QueryKeys.userLicences, productName],
      queryFn: () =>
      fetchUserLicences(),
    });

  const datasetDefinitions: UseQueryResult<DatasetDefinitions> = useQuery({
      queryKey: [QueryKeys.definitionsKey],
      queryFn: () => fetchDefinitions(),
    });

  const isLoading: Boolean =
    catalogueEntryQuery.isPending ||
    datasetDefinitions.isPending ||
    userLicences.isPending;
  const isError: Boolean =
    catalogueEntryQuery.isError ||
    datasetDefinitions.isError ||
    userLicences.isError;
  const isSuccess: Boolean =
    catalogueEntryQuery.isSuccess &&
    datasetDefinitions.isSuccess &&
    userLicences.isSuccess;

  useEffect(() => {
    // Reverse map NATIONAL X back to backend key for licence check
    const backendProductName = REVERSE_PRODUCT_MAPPING[productName] || productName;

    const isProductLicensed = userLicences.data?.dataset_products.some(
      (product: any) => {
        return product.product_name === backendProductName;
      }
    );

    if (isProductLicensed) {
      setLicenced(true);
    }
  }, [isSuccess]);

  var datasetId = "";
  if (isSuccess) {
    // Reverse map NATIONAL X back to backend key for metadata lookup
    let backendId = REVERSE_PRODUCT_MAPPING[catalogueEntryQuery.data.id] || catalogueEntryQuery.data.id;

    datasetId = (
      backendId === "GEOSCAPE PLANNING INSIGHTS"
        ? "GEOSCAPE PLANNING INSIGHTS - BASE"
        : backendId
    ) as Product;
  }

  return (
    <Box sx={{ position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          alignContent: "space-between",

          gap: "16px",
          height: "43px",
          position: "absolute",
          top: "-55px",
          right: "0px",
        }}
      >
        <BackButton />
      </Box>
      <Grid container columnSpacing={"16px"}>
        <Grid item xs={8}>
          <ReactQueryErrorWrapper
            queries={[catalogueEntryQuery, userLicences, datasetDefinitions]}
            mutations={[]}
          />

          {isLoading && <Skeleton variant="rectangular" height="500px" />}

          {isSuccess && (
            <Stack
              direction="column"
              sx={{
                gap: "16px",
              }}
            >
              <Card>
                <CardHeader
                  title={`About ${catalogueEntryQuery.data.displayProduct}`}
                  subheader={catalogueEntryQuery.data.subtitle}
                ></CardHeader>
                <CardContent>
                  <ShowMore>
                    <StyledMarkdown>
                      {catalogueEntryQuery.data.description}
                    </StyledMarkdown>
                  </ShowMore>
                </CardContent>
              </Card>
              <CatalogueEntryCaseStudies
                caseStudies={catalogueEntryQuery.data.caseStudies}
              />
              <CatalogueEntryDataDictionary
                dataDictionaries={catalogueEntryQuery.data.dataDictionaries}
              />
            </Stack>
          )}
        </Grid>
        <Grid item xs={4}>
          {isLoading && <Skeleton variant="rectangular" height="500px" />}
          {isSuccess && (
            <Stack
              direction={"column"}
              sx={{
                gap: "16px",
              }}
            >
              <Card>
                <CardHeader title="I want to..."></CardHeader>

                <CardContent style={{ textAlign: "center" }}>
                  <CustomButtonGroup
                    licenced={licenced}
                    displayProduct={productName}
                  />
                </CardContent>
              </Card>
              <CatalogueEntryMetadata
                datasetId={datasetId}
                image={`data:image/png;base64,${
                  isDark
                    ? catalogueEntryQuery.data.imageDark
                    : catalogueEntryQuery.data.imageLight
                }`}
                metadata={catalogueEntryQuery.data.metadata}
                definitionsMetadata={
                  datasetDefinitions.data?.metadata[datasetId]
                }
                latestReleaseReportLink={
                  catalogueEntryQuery.data.latestReleaseReportLink
                }
                productDescriptionLink={
                  catalogueEntryQuery.data.productDescriptionLink
                }
              />
            </Stack>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};
